import { SmgElement } from '../core/smg-element';
import { html, when, type SafeHtml } from '../core/safe-html';
import { navigate, route, type Route } from '../core/router';
import { initials, nfmt } from '../core/format';
import { auth, session } from '../services/auth-service';
import { fleetTotal } from '../services/device-service';
import { ROLE_ICONS, ROLE_LABELS } from '../models/user';

interface NavItem {
  screen: string;
  path: string;
  icon: string;
  label: string;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { screen: 'devices', path: '#/devices', icon: 'ti ti-devices', label: 'Cihazlar' },
  { screen: 'brands', path: '#/brands', icon: 'ti ti-photo', label: 'Marka & imaj' },
  { screen: 'scripts', path: '#/scripts', icon: 'ti ti-terminal-2', label: 'Scriptler' },
  { screen: 'users', path: '#/users', icon: 'ti ti-users', label: 'Kullanıcılar', adminOnly: true },
  { screen: 'wg', path: '#/wg', icon: 'ti ti-shield-lock', label: 'WireGuard', adminOnly: true },
  { screen: 'settings', path: '#/settings', icon: 'ti ti-settings', label: 'Ayarlar' },
];

const CRUMBS: Record<string, [string, string]> = {
  devices: ['Filo', 'Cihazlar'],
  'device-detail': ['Filo', 'Cihaz detayı'],
  brands: ['Yönetim', 'Marka & imaj'],
  scripts: ['Yönetim', 'Scriptler'],
  users: ['Yönetim', 'Kullanıcılar'],
  wg: ['Yönetim', 'WireGuard erişimi'],
  settings: ['Yönetim', 'Ayarlar'],
};

/** Uygulama kabuğu: oturum kapıları + gezinme + görünüm yuvası. */
export class AppShell extends SmgElement {
  protected override onConnected(): void {
    this.bind(session);
    this.bind(route);
    this.bind(fleetTotal);
    this.onAct('click', {
      nav: (el) => navigate(el.dataset['path'] ?? '#/devices'),
      logout: () => void auth.logout().then(() => navigate('#/login')),
    });
  }

  private isNavActive(item: NavItem, current: Route): boolean {
    if (item.screen === 'devices') {
      return current.screen === 'devices' || current.screen === 'device-detail';
    }
    return current.screen === item.screen;
  }

  protected override template(): SafeHtml {
    const s = session.get();
    const current = route.get();

    if (s.loading) {
      return html`
        <div style="height:100%;display:flex;align-items:center;justify-content:center;background:var(--vx-bg)">
          <i class="ti ti-loader-2 spin" style="font-size:30px;color:var(--vx-primary)"></i>
        </div>`;
    }

    if (!s.user) {
      return html`<smg-login-view></smg-login-view>`;
    }

    // Girişliyken login rotası ve yetkisiz rota düzeltmeleri
    if (current.screen === 'login') {
      queueMicrotask(() => navigate('#/devices'));
    }
    if ((current.screen === 'users' || current.screen === 'wg') && !s.claims?.users) {
      queueMicrotask(() => navigate('#/devices'));
    }

    const visibleNav = NAV.filter((n) => !n.adminOnly || s.claims?.users);
    const [crumbRoot, crumbLeaf] = CRUMBS[current.screen] ?? CRUMBS['devices']!;
    const total = fleetTotal.get();
    const roleLabel = ROLE_LABELS[s.user.role];

    return html`
      <div class="shell">
        <aside class="sidebar">
          <div style="display:flex;align-items:center;gap:9px;padding:0 4px 18px">
            <span style="width:30px;height:30px;border-radius:8px;background:var(--vx-primary);display:flex;align-items:center;justify-content:center;flex:none">
              <i class="ti ti-server-cog" style="font-size:18px;color:#fff"></i>
            </span>
            <span style="font:600 14.5px var(--vx-font-sans)">Filo paneli</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:3px">
            ${visibleNav.map((n) => html`
              <button class="navbtn ${this.isNavActive(n, current) ? 'on' : ''}" data-act="nav" data-path="${n.path}">
                <i class="${n.icon}"></i>${n.label}
                ${when(n.screen === 'devices' && total != null, () => html`
                  <span class="mono" style="margin-left:auto;font-size:11px;color:var(--vx-text-3)">${nfmt(total!)}</span>`)}
              </button>`)}
          </div>
          <div style="margin-top:auto;padding:11px 12px;background:var(--vx-surface-2);border:1px solid var(--vx-border);border-radius:var(--vx-radius-md)">
            <div style="font:500 10px var(--vx-font-sans);color:var(--vx-text-3);text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">Oturum</div>
            <div style="display:flex;align-items:center;gap:8px">
              <span style="width:28px;height:28px;border-radius:50%;background:var(--vx-brand-50);color:var(--vx-brand-700);display:flex;align-items:center;justify-content:center;font:600 11px var(--vx-font-sans);flex:none">${initials(s.user.username)}</span>
              <div style="min-width:0;flex:1">
                <div style="font:600 12.5px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${s.user.username}</div>
                <div style="font:400 10.5px var(--vx-font-sans);color:var(--vx-text-3)">${roleLabel}</div>
              </div>
              <button data-act="logout" title="Oturumu kapat" style="border:none;background:none;cursor:pointer;color:var(--vx-text-3);display:flex;padding:4px">
                <i class="ti ti-logout" style="font-size:17px"></i>
              </button>
            </div>
          </div>
        </aside>

        <div class="shell-main">
          <div class="topbar">
            <span style="font:400 13px var(--vx-font-sans);color:var(--vx-text-3)">${crumbRoot}</span>
            <span style="color:var(--vx-text-3)">/</span>
            <span style="font:600 13px var(--vx-font-sans)">${crumbLeaf}</span>
            <div style="margin-left:auto;display:flex;align-items:center;gap:10px">
              <span class="fchip" style="cursor:default"><i class="${ROLE_ICONS[s.user.role]}" style="font-size:14px;color:var(--vx-primary)"></i>${roleLabel}</span>
              <span style="width:32px;height:32px;border-radius:50%;background:var(--vx-brand-50);color:var(--vx-brand-700);display:flex;align-items:center;justify-content:center;font:600 12px var(--vx-font-sans)">${initials(s.user.username)}</span>
            </div>
          </div>

          ${this.viewFor(current)}

          <div class="tabbar">
            ${visibleNav.map((n) => html`
              <button class="tabbtn ${this.isNavActive(n, current) ? 'on' : ''}" data-act="nav" data-path="${n.path}">
                <i class="${n.icon}"></i>${n.label}
              </button>`)}
          </div>
        </div>

        <smg-toast></smg-toast>
        <smg-confirm></smg-confirm>
      </div>`;
  }

  private viewFor(current: Route): SafeHtml {
    switch (current.screen) {
      case 'device-detail':
        return html`<smg-device-detail data-serial="${current.param ?? ''}"></smg-device-detail>`;
      case 'brands':
        return html`<smg-brands-view></smg-brands-view>`;
      case 'scripts':
        return html`<smg-scripts-view></smg-scripts-view>`;
      case 'users':
        return html`<smg-users-view></smg-users-view>`;
      case 'wg':
        return html`<smg-wg-view></smg-wg-view>`;
      case 'settings':
        return html`<smg-settings-view></smg-settings-view>`;
      default:
        return html`<smg-devices-view></smg-devices-view>`;
    }
  }
}

customElements.define('smg-app', AppShell);
